import { useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { useCreateBooking } from "../../../../api/fleet-management/bookings/booking.hooks";
import { useGetVehicles } from "../../../../api/fleet-management/vehicles/vehicles.hooks";
import BookingVehiclesSection from "./_components/BookingVehiclesSection";
import {
  validateBookingForm,
  formatCreateBookingPayload,
} from "./utils/createBooking.utils";

const initialForm = {
  vehicles: [{ vehicleId: "", packageId: "" }],
  fullName: "",
  companyName: "",
  phoneNumber: "",
  emergencyContactNumber: "",
  email: "",
  pickupLocation: "",
  dropoffLocation: "",
  pickupDateTime: "",
  dropoffDateTime: "",
};

const CreateBookingPage = () => {
  const history = useHistory();
  const { data: vehiclesData, isLoading: isLoadingVehicles } = useGetVehicles();
  const { mutate: createBooking, isLoading: isCreatingBooking } =
    useCreateBooking();

  const [form, setForm] = useState(initialForm);
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [fieldErrors, setFieldErrors] = useState({});

  const vehicles = Array.isArray(vehiclesData)
    ? vehiclesData
    : vehiclesData?.vehicles || vehiclesData?.items || [];

  const selectedVehicleIds = form.vehicles
    .map((vehicle) => vehicle?.vehicleId)
    .filter(Boolean);

  const handleInputChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const handleVehicleChange = (index, field, value) => {
    setForm((prev) => {
      const updatedVehicles = [...prev.vehicles];
      updatedVehicles[index] = {
        ...updatedVehicles[index],
        [field]: value,
        ...(field === "vehicleId" ? { packageId: "" } : {}),
      };
      return { ...prev, vehicles: updatedVehicles };
    });

    const errorKeys = [`vehicle_${index}_id`, `vehicle_${index}_packageId`];
    if (errorKeys.some((errorKey) => fieldErrors[errorKey])) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        errorKeys.forEach((errorKey) => {
          delete newErrors[errorKey];
        });
        return newErrors;
      });
    }
  };

  const addVehicle = () => {
    setForm((prev) => ({
      ...prev,
      vehicles: [...prev.vehicles, { vehicleId: "", packageId: "" }],
    }));
  };

  const removeVehicle = (index) => {
    if (form.vehicles.length === 1) return;
    setForm((prev) => ({
      ...prev,
      vehicles: prev.vehicles.filter((_, i) => i !== index),
    }));
  };



  const handleSubmit = () => {
    setFeedback({ type: "", message: "" });

    const errors = validateBookingForm(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFeedback({
        type: "warning",
        message: "Please fix the errors in the form.",
      });
      return;
    }

    const bookingPayload = formatCreateBookingPayload(form);

    createBooking(bookingPayload, {
      onSuccess: () => {
        setFeedback({
          type: "success",
          message: "Booking created successfully! Redirecting...",
        });
        setTimeout(() => {
          history.push("/fleet-management/bookings");
        }, 1500);
      },
      onError: (error) => {
        setFeedback({
          type: "danger",
          message:
            error?.response?.data?.message || "Failed to create booking.",
        });
      },
    });
  };

  if (isLoadingVehicles) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">Create New Booking</h5>
        </div>

        <div className="card-body">
          {feedback.message && (
            <Alert variant={feedback.type} className="mb-4" dismissible>
              {feedback.message}
            </Alert>
          )}

          <Form>
            {/* Customer Details Section */}
            <Row className="g-3 mb-4">
              <Col>
                <h5 className="mb-3">Customer Details</h5>
              </Col>
            </Row>

            <Row className="g-3 mb-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="form-label">
                    Full Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={form.fullName}
                    onChange={(e) =>
                      handleInputChange("fullName", e.target.value)
                    }
                    disabled={isCreatingBooking}
                    placeholder="Enter full name"
                    isInvalid={!!fieldErrors.fullName}
                  />
                  {fieldErrors.fullName && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.fullName}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="form-label">Company Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.companyName}
                    onChange={(e) =>
                      handleInputChange("companyName", e.target.value)
                    }
                    disabled={isCreatingBooking}
                    placeholder="Enter company name"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="form-label">
                    Phone Number <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    value={form.phoneNumber}
                    onChange={(e) =>
                      handleInputChange("phoneNumber", e.target.value)
                    }
                    disabled={isCreatingBooking}
                    placeholder="Enter phone number"
                    isInvalid={!!fieldErrors.phoneNumber}
                  />
                  {fieldErrors.phoneNumber && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.phoneNumber}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="form-label">
                    Email <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    value={form.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    disabled={isCreatingBooking}
                    placeholder="Enter email address"
                    isInvalid={!!fieldErrors.email}
                  />
                  {fieldErrors.email && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.email}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="form-label">Emergency Contact Number</Form.Label>
                  <Form.Control
                    type="tel"
                    value={form.emergencyContactNumber}
                    onChange={(e) =>
                      handleInputChange("emergencyContactNumber", e.target.value)
                    }
                    disabled={isCreatingBooking}
                    placeholder="Enter emergency contact number"
                    isInvalid={!!fieldErrors.emergencyContactNumber}
                  />
                  {fieldErrors.emergencyContactNumber && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.emergencyContactNumber}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {/* Trip Details Section */}
            <Row className="g-3 mb-4">
              <Col>
                <h5 className="mb-3">Trip Details</h5>
              </Col>
            </Row>

            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="form-label">
                    Pickup Location <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={form.pickupLocation}
                    onChange={(e) =>
                      handleInputChange("pickupLocation", e.target.value)
                    }
                    disabled={isCreatingBooking}
                    placeholder="Enter pickup location"
                    isInvalid={!!fieldErrors.pickupLocation}
                  />
                  {fieldErrors.pickupLocation && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.pickupLocation}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="form-label">
                    Dropoff Location <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={form.dropoffLocation}
                    onChange={(e) =>
                      handleInputChange("dropoffLocation", e.target.value)
                    }
                    disabled={isCreatingBooking}
                    placeholder="Enter dropoff location"
                    isInvalid={!!fieldErrors.dropoffLocation}
                  />
                  {fieldErrors.dropoffLocation && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.dropoffLocation}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="form-label">
                    Pickup Date & Time <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={form.pickupDateTime}
                    onChange={(e) =>
                      handleInputChange("pickupDateTime", e.target.value)
                    }
                    disabled={isCreatingBooking}
                    isInvalid={!!fieldErrors.pickupDateTime}
                  />
                  {fieldErrors.pickupDateTime && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.pickupDateTime}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="form-label">
                    Dropoff Date & Time <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={form.dropoffDateTime}
                    onChange={(e) =>
                      handleInputChange("dropoffDateTime", e.target.value)
                    }
                    disabled={isCreatingBooking}
                    isInvalid={!!fieldErrors.dropoffDateTime}
                  />
                  {fieldErrors.dropoffDateTime && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.dropoffDateTime}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <BookingVehiclesSection
              form={form}
              fieldErrors={fieldErrors}
              vehicles={vehicles}
              selectedVehicleIds={selectedVehicleIds}
              isCreatingBooking={isCreatingBooking}
              onVehicleChange={handleVehicleChange}
              onRemoveVehicle={removeVehicle}
              onAddVehicle={addVehicle}
            />
          </Form>
        </div>

        <div className="card-footer">
          <Button
            variant="secondary"
            onClick={() => history.push("/fleet-management/bookings")}
            disabled={isCreatingBooking}
            className="me-2"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isCreatingBooking}
          >
            {isCreatingBooking ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              "Create Booking"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateBookingPage;
